import React from 'react';
import type { DeepReadonly } from '@itandi/itandi-bb-ui';
import {
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modalv2
} from '@itandi/itandi-bb-ui';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { NotificationModalForm } from '~/components/common/notifications/settings/NotificationModalForm';
import type { Status } from '~/models/Status';
import type {
  Notification,
  NotificationEdit,
  NotificationTargetType
} from '~/models/Notification';
import { buildInitialNotification, createSchema } from '~/models/Notification';
import type { Subtype } from '~/models/Subtype';
import type { LineUserAccount } from '~/models/LineUserAccount';
import { onInvalidScroll } from '~/utils/FormUtil';

type Props = DeepReadonly<{
  type: NotificationTargetType;
  handleClose: () => void;
  statuses: Status[];
  subtypes: Subtype[] | null;
  lineUserAccounts: LineUserAccount[] | null;
  useLineNotification: boolean;
  notification?: Notification | null;
  handleAcceptSubmit: (notification: NotificationEdit) => Promise<void>;
}>;

export const NotificationModal: React.FC<Props> = ({
  type,
  handleClose,
  statuses,
  subtypes,
  lineUserAccounts,
  useLineNotification,
  notification = null,
  handleAcceptSubmit
}: Props) => {
  const methods = useForm<NotificationEdit>({
    defaultValues: notification ?? buildInitialNotification(),
    mode: 'onBlur',
    resolver: yupResolver(createSchema(type)),
    shouldFocusError: false
  });
  const handleSubmit = React.useCallback(() => {
    methods.handleSubmit(
      async () => {
        handleAcceptSubmit(methods.getValues()).then();
      },
      (errors) => onInvalidScroll(errors)
    )();
  }, [methods, handleAcceptSubmit]);
  return (
    <Modalv2 isOpen onClose={handleClose} size="l">
      <ModalHeader title="通知設定" />
      <ModalBody>
        <FormProvider {...methods}>
          <NotificationModalForm
            lineUserAccounts={lineUserAccounts}
            statuses={statuses}
            subtypes={subtypes}
            type={type}
            useLineNotification={useLineNotification}
          />
        </FormProvider>
      </ModalBody>
      <ModalFooter
        leftArea={
          <Button onClick={handleClose} variant="secondary">
            キャンセル
          </Button>
        }
        rightArea={
          <Button onClick={handleSubmit} variant="primary">
            {notification ? '更新' : '追加'}
          </Button>
        }
      />
    </Modalv2>
  );
};
